import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api_config';
import WellbeingCheckin from '../components/WellbeingCheckin';

// Canonical MindStream colours — Power=Blue, Flow=Red, Clarity=Green.
// Never gold; the Golden Thread is a metaphor, not a stream colour.
const STREAM = {
  Power:   '#4f46e5',
  Flow:    '#ef4444',
  Clarity: '#059669',
};

const bg      = '#1a1a2e';
const surface = '#23233a';
const inset   = '#1b1b2d';
const line    = '#2f2f48';
const ink     = '#e8e8f0';
const muted   = '#9a9ab0';
const dim     = '#64748b';
const accent  = '#f5c542';
const blue    = '#3b82f6';

const DOMAINS = [
  { key: 'D',   label: 'Dopaminergic Freedom',   short: 'Dopaminergic',  stream: 'Power' },
  { key: 'OXY', label: 'Oxytocinergic Resonance', short: 'Oxytocinergic', stream: 'Flow' },
  { key: 'SE',  label: 'Serotonergic Stability',  short: 'Serotonergic',  stream: 'Flow' },
  { key: 'REN', label: 'Endorphin Oneness',       short: 'Endorphin',     stream: 'Clarity' },
];

const JourneyDashboard = () => {
  const navigate = useNavigate();
  const [journey, setJourney]         = useState(null);
  const [dxi, setDxi]                 = useState(null);
  const [today, setToday]             = useState(null);
  const [showCheckin, setShowCheckin] = useState(false);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [drawn, setDrawn]             = useState(false);

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (dxi) {
      const t = setTimeout(() => setDrawn(true), 120);
      return () => clearTimeout(t);
    }
  }, [dxi]);

  const load = async () => {
    try {
      setLoading(true);
      const [j, d, t] = await Promise.all([
        API.get('/api/v1/journey'),
        API.get('/api/v1/journey/dxi'),
        API.get('/api/v1/journey/dxi/today').catch(() => null),
      ]);
      setJourney(j.data);
      setDxi(d.data);
      if (t) setToday(t.data);
      try {
        const a = await API.get('/api/v1/journey/assessment/status');
        if (a.data?.needs_journey_assessment) setShowCheckin(true);
      } catch (_) { /* optional */ }
    } catch (e) {
      console.error('journey load failed', e);
      setError('Your journey could not load. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const days = journey?.days_on_journey ?? journey?.journey?.days_on_journey ?? null;
  const trendWord = { rising: 'rising', steady: 'steady', settling: 'settling' }[dxi?.trend] || '';

  // ── The dial ──────────────────────────────────────────────────────────
  // A 270° sweep from bottom-left round the top to bottom-right, with a
  // needle. It counts SESSIONS: one session is 0.025 of a domain, so a
  // points dial would barely leave zero on a real day. The cards beneath
  // carry the points, unscaled.
  const dial = (fraction, sessions, target) => {
    const CX = 85, CY = 85, R = 66, RN = 54;
    const f = Math.max(0, Math.min(fraction || 0, 1));
    const deg = (a) => (a * Math.PI) / 180;
    const px = (r, a) => CX + r * Math.cos(deg(a));
    const py = (r, a) => CY + r * Math.sin(deg(a));
    const start = 135, sweep = 270;
    const end = start + sweep * f;
    const largeFill = sweep * f > 180 ? 1 : 0;
    const ticks = [135, 202.5, 270, 337.5, 405];
    return (
      <svg width="170" height="150" viewBox="0 0 170 150" role="img"
           aria-label={`${sessions} of ${target} sessions today`}>
        <circle cx={CX} cy={CY} r="78" fill={inset} stroke={line} strokeWidth="2" />
        <path d={`M ${px(R, start)},${py(R, start)} A ${R},${R} 0 1,1 ${px(R, start + sweep)},${py(R, start + sweep)}`}
              fill="none" stroke="#222235" strokeWidth="10" strokeLinecap="round" />
        {f > 0.01 && (
          <path d={`M ${px(R, start)},${py(R, start)} A ${R},${R} 0 ${largeFill},1 ${px(R, end)},${py(R, end)}`}
                fill="none" stroke={blue} strokeWidth="10" strokeLinecap="round"
                style={{ filter: 'drop-shadow(0 0 4px rgba(59,130,246,0.6))', transition: 'all 700ms ease-out' }} />
        )}
        {ticks.map((a, i) => (
          <line key={a}
                x1={px(R - 12, a)} y1={py(R - 12, a)} x2={px(R - 20, a)} y2={py(R - 20, a)}
                stroke={i === 2 ? '#60a5fa' : '#475569'} strokeWidth="2" />
        ))}
        <line x1={CX} y1={CY} x2={px(RN, end)} y2={py(RN, end)}
              stroke={blue} strokeWidth="3" strokeLinecap="round"
              style={{ transition: 'all 700ms ease-out' }} />
        <circle cx={CX} cy={CY} r="7" fill="#ffffff" />
        <circle cx={CX} cy={CY} r="3" fill="#1e1e2e" />
        <text x={CX} y={CY + 36} textAnchor="middle" fill={ink}
              fontSize="20" fontWeight="800" fontVariantNumeric="tabular-nums">
          {sessions}
        </text>
        <text x={CX} y={CY + 50} textAnchor="middle" fill={muted}
              fontSize="9" fontWeight="600" letterSpacing="0.1em">
          {`SESSION${sessions === 1 ? '' : 'S'} TODAY`}
        </text>
      </svg>
    );
  };

  if (loading) {
    return (
      <div style={S.page}><div style={S.wrap}><p style={{ color: muted }}>Loading your journey…</p></div></div>
    );
  }

  if (error || !dxi) {
    return (
      <div style={S.page}>
        <div style={S.wrap}>
          <p style={{ color: ink }}>{error || 'Your journey could not load.'}</p>
          <button style={S.button} onClick={load}>Try again</button>
        </div>
      </div>
    );
  }

  const hasToday = today && typeof today.sessions === 'number';

  return (
    <div style={S.page}>
      <div style={S.wrap}>

        <header style={S.header}>
          <div>
            <h1 style={S.title}>Your FHO Journey</h1>
            {days !== null && <span style={S.days}>{days} days</span>}
          </div>
          <button style={S.linkButton} onClick={() => navigate('/dashboard')}>
            Back to dashboard
          </button>
        </header>

        {/* ── The instrument: two faces of one index ─────────────────── */}
        <section style={S.card}>
          <div style={S.columns}>

            {/* LEFT — 30-day */}
            <div style={S.col}>
              <span style={S.badge}>30-DAY INDEX</span>

              <div style={S.heroRow}>
                <span style={S.composite}>{dxi.composite}</span>
                <div style={S.heroText}>
                  <div style={S.band}>{dxi.band}</div>
                  <div style={S.trend}>Doxyseren Index, {trendWord}</div>
                </div>
              </div>

              <ul style={S.list}>
                {DOMAINS.map(d => {
                  const dom = dxi.domains[d.key] || {};
                  const score = dom.score ?? 0;
                  return (
                    <li key={d.key} style={S.row}>
                      <div style={S.rowTop}>
                        <span style={S.rowName}>
                          {d.label}
                          <span style={{ ...S.rowStream, color: STREAM[d.stream] }}> {d.stream}</span>
                        </span>
                        <span style={S.rowScore}>{score}<span style={S.of}> /25</span></span>
                      </div>
                      <div style={S.track}>
                        <div style={{
                          ...S.fill,
                          background: STREAM[d.stream],
                          width: drawn ? `${(score / 25) * 100}%` : '0%',
                        }} />
                      </div>
                    </li>
                  );
                })}
              </ul>

              <p style={S.footnote}>30-day cumulative window</p>
            </div>

            <div style={S.separator} />

            {/* RIGHT — today */}
            <div style={S.col}>
              <span style={S.badgeLive}>
                <span style={S.liveDot} />
                DAILY CHECK-IN
              </span>

              {hasToday ? (
                <>
                  <div style={S.dialWrap}>
                    {dial(today.fraction, today.sessions, today.session_target || 10)}
                  </div>

                  <ul style={S.miniGrid}>
                    {DOMAINS.map(d => {
                      const v = (today.gains && today.gains[d.key]) || 0;
                      return (
                        <li key={d.key} style={S.mini}>
                          <span style={{ ...S.miniDot, background: STREAM[d.stream], opacity: v > 0 ? 1 : 0.3 }} />
                          <div>
                            <div style={S.miniLabel}>{d.short}</div>
                            <div style={{ ...S.miniValue, color: v > 0 ? ink : dim }}>
                              +{v.toFixed(3)} pts
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>

                  {today.sessions === 0 && (
                    <p style={S.footnote}>No sessions yet today. The dial fills as you practise.</p>
                  )}
                </>
              ) : (
                <p style={S.footnote}>Today's activity will appear here.</p>
              )}
            </div>

          </div>
        </section>

        <section style={S.block}>
          <p style={S.focusLine}>
            Your lowest domain is <strong style={{ color: STREAM[dxi.focus.stream] }}>{dxi.focus.domain_name}</strong>,
            on the {dxi.focus.stream} MindStream. {dxi.focus.coach} works that stream.
          </p>
          <button style={S.button} onClick={() => navigate(dxi.focus.path)}>
            Open {dxi.focus.coach}
          </button>
        </section>

        <section style={S.block}>
          <p style={S.small}>
            A short check-in feeds the index with how you actually feel, not just how often you show up.
          </p>
          <button style={S.buttonQuiet} onClick={() => setShowCheckin(true)}>Check in</button>
        </section>

        <footer style={S.footer}>
          <p style={S.disclosure}>{dxi.disclosure}</p>
          <a href="https://doxyseren.com" target="_blank" rel="noopener noreferrer" style={S.doxButton}>
            What is DOXYSEREN&trade;?
          </a>
        </footer>
      </div>

      <WellbeingCheckin
        isOpen={showCheckin}
        onClose={() => setShowCheckin(false)}
        onComplete={() => load()}
        authToken={localStorage.getItem('token')}
      />
    </div>
  );
};

const S = {
  page: {
    minHeight: '100vh', background: bg, color: ink,
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    padding: '32px 20px',
  },
  wrap: { maxWidth: 780, margin: '0 auto' },

  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 },
  title: { fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.01em' },
  days: {
    display: 'inline-block', marginTop: 8, padding: '3px 10px',
    background: surface, border: `1px solid ${line}`, borderRadius: 999, fontSize: 13, color: muted,
  },
  linkButton: {
    background: 'linear-gradient(135deg, #2E7D32, #4CAF50)', border: 'none', color: '#fff',
    fontSize: 14, fontWeight: 500, cursor: 'pointer', padding: '9px 16px', borderRadius: 8,
    textDecoration: 'none', whiteSpace: 'nowrap',
  },

  card: {
    background: `linear-gradient(135deg, ${inset} 0%, #121220 100%)`,
    border: `1.5px solid ${line}`, borderRadius: 16, padding: 22, marginBottom: 20,
  },
  columns: { display: 'flex', flexWrap: 'wrap', gap: 0 },
  col: { flex: '1 1 300px', minWidth: 0, padding: '2px 4px' },
  separator: {
    flex: '0 0 1px', alignSelf: 'stretch', margin: '4px 22px',
    borderLeft: `1.5px dashed ${line}`,
  },

  badge: {
    display: 'inline-block', padding: '4px 12px', borderRadius: 11,
    background: '#26263a', color: '#94a3b8', fontSize: 10, fontWeight: 700,
    letterSpacing: '0.06em', marginBottom: 18,
  },
  badgeLive: {
    display: 'inline-flex', alignItems: 'center', gap: 8, padding: '4px 12px 4px 8px',
    borderRadius: 11, background: '#1e293b', border: `1px solid ${blue}`,
    color: '#60a5fa', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 14,
  },
  liveDot: {
    width: 8, height: 8, borderRadius: '50%', background: blue,
    boxShadow: '0 0 0 3px rgba(59,130,246,0.25), 0 0 8px rgba(59,130,246,0.6)',
  },

  heroRow: { display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 18 },
  composite: {
    fontSize: 52, fontWeight: 800, lineHeight: 1, letterSpacing: '-0.03em',
    color: accent, fontVariantNumeric: 'tabular-nums',
  },
  heroText: { display: 'flex', flexDirection: 'column', gap: 3 },
  band: { fontSize: 16, fontWeight: 700 },
  trend: { fontSize: 11, color: muted, fontWeight: 500 },

  list: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 },
  row: {},
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 },
  rowName: { fontSize: 11, fontWeight: 600, color: '#cbd5e1' },
  rowStream: { fontSize: 9, fontWeight: 700, marginLeft: 6 },
  rowScore: { fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  of: { color: dim, fontSize: 9, fontWeight: 400 },
  track: { height: 5, background: '#28283d', borderRadius: 3, overflow: 'hidden' },
  fill: { height: '100%', borderRadius: 3, transition: 'width 600ms ease-out' },
  footnote: { margin: '14px 0 0', fontSize: 10, color: dim, fontStyle: 'italic' },

  dialWrap: { display: 'flex', justifyContent: 'center', margin: '2px 0 10px' },
  miniGrid: {
    listStyle: 'none', margin: 0, padding: 0,
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
  },
  mini: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
    borderRadius: 8, background: '#171725', border: '1px solid #26263a',
  },
  miniDot: { width: 8, height: 8, borderRadius: '50%', flex: 'none' },
  miniLabel: { fontSize: 9, fontWeight: 600, color: '#94a3b8' },
  miniValue: { fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },

  block: { padding: '18px 0', borderTop: `1px solid ${line}` },
  focusLine: { margin: '0 0 14px', fontSize: 15, lineHeight: 1.55 },
  small: { margin: '0 0 12px', fontSize: 14, lineHeight: 1.55, color: ink },

  button: {
    background: accent, color: '#1a1a2e', border: 'none', borderRadius: 8,
    padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  buttonQuiet: {
    background: 'none', color: ink, border: `1px solid ${line}`, borderRadius: 8,
    padding: '10px 16px', fontSize: 14, cursor: 'pointer',
  },
  doxButton: {
    display: 'block', marginTop: 18, padding: '12px 16px',
    background: 'none', color: accent, border: `1px solid ${accent}`,
    borderRadius: 8, fontSize: 14, fontWeight: 600,
    textDecoration: 'none', textAlign: 'center', letterSpacing: '0.01em',
  },

  footer: {},
  disclosure: { margin: 0, fontSize: 12, lineHeight: 1.5, color: muted },
};

export default JourneyDashboard;
